import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/cn';
import { motionSafe, springSheet } from '@/lib/motion';

export interface ContextMenuItem {
  key: string;
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  /** Shown under the label when disabled, a menu that refuses must say why. */
  reason?: string;
}

interface Props {
  x: number;
  y: number;
  label: string;
  items: ContextMenuItem[];
  onClose: () => void;
  /**
   * The control that opened this menu, when there is one.
   *
   * Dismissal ignores pointerdowns on it so the trigger can toggle: otherwise
   * pressing it again dismisses on pointerdown and reopens on click, and the
   * menu can never be closed the same way it was opened. Right-click menus
   * have no trigger element and leave this unset.
   */
  triggerRef?: React.RefObject<HTMLElement | null>;
}

const EDGE = 8;

/**
 * Right-click menu, positioned at the pointer and kept on screen.
 *
 * Keyboard-operable because a context menu that only answers to the right
 * mouse button is a set of actions some people simply cannot reach: arrows
 * move, Enter picks, Escape leaves, focus returns where it came from.
 */
export function ContextMenu({ x, y, label, items, onClose, triggerRef }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);
  const [pos, setPos] = useState({ x, y });
  const enabled = items.filter((i) => !i.disabled);
  const [activeKey, setActiveKey] = useState(enabled[0]?.key ?? null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Flip rather than clamp: a menu shoved back inside the viewport ends up
    // under the pointer and swallows the next click.
    const nextX = x + rect.width > window.innerWidth - EDGE ? x - rect.width : x;
    const nextY = y + rect.height > window.innerHeight - EDGE ? y - rect.height : y;
    setPos({
      x: Math.max(EDGE, nextX),
      y: Math.max(EDGE, nextY),
    });
  }, [x, y]);

  useEffect(() => {
    returnFocusTo.current = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => returnFocusTo.current?.focus?.();
  }, []);

  useEffect(() => {
    const dismiss = (event: Event) => {
      // A pointerdown on the menu's own items must not dismiss it. The
      // listener below is in capture phase on window, so it runs *before* the
      // item's React handler -- without this check the menu unmounts on
      // pointerdown and the click never lands, leaving every item unclickable.
      const target = event.target;
      if (target instanceof Node) {
        if (ref.current?.contains(target)) return;
        if (triggerRef?.current?.contains(target)) return;
      }
      onClose();
    };
    // Capture phase: close before the click lands on whatever is underneath.
    window.addEventListener('pointerdown', dismiss, true);
    window.addEventListener('blur', dismiss);
    window.addEventListener('resize', dismiss);
    document.addEventListener('scroll', dismiss, true);
    return () => {
      window.removeEventListener('pointerdown', dismiss, true);
      window.removeEventListener('blur', dismiss);
      window.removeEventListener('resize', dismiss);
      document.removeEventListener('scroll', dismiss, true);
    };
  }, [onClose, triggerRef]);

  const move = (delta: number) => {
    if (enabled.length === 0) return;
    const current = enabled.findIndex((i) => i.key === activeKey);
    const next = (current + delta + enabled.length) % enabled.length;
    setActiveKey(enabled[next]!.key);
  };

  return (
    <motion.div
      ref={ref}
      role="menu"
      aria-label={label}
      tabIndex={-1}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={motionSafe(springSheet)}
      style={{ left: pos.x, top: pos.y, transformOrigin: 'top left' }}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          move(1);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          move(-1);
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const item = enabled.find((i) => i.key === activeKey);
          if (item) {
            item.onSelect();
            onClose();
          }
        }
      }}
      className={cn(
        'material fixed z-overlay flex min-w-[12rem] flex-col gap-1 rounded-lg border border-border p-1',
        'shadow-lifted outline-none',
      )}
    >
      {items.map((item) => {
        const Icon = item.icon;
        const active = item.key === activeKey && !item.disabled;
        return (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            aria-describedby={item.disabled && item.reason ? `${item.key}-reason` : undefined}
            onPointerEnter={() => !item.disabled && setActiveKey(item.key)}
            onClick={() => {
              if (item.disabled) return;
              item.onSelect();
              onClose();
            }}
            className={cn(
              'flex w-full items-center gap-3 rounded px-3 py-2 text-left text-small',
              item.disabled
                ? 'cursor-not-allowed text-text-tertiary opacity-60'
                : 'text-text',
              active && 'bg-surface-2',
              !item.disabled && item.tone === 'danger' && 'text-danger',
            )}
          >
            {/* Fixed-width gutter: icons vary in optical width, and without one
                the labels step in and out by a pixel or two down the list. */}
            <span className="flex size-icon shrink-0 items-center justify-center" aria-hidden>
              <Icon size={14} strokeWidth={1.5} />
            </span>
            <span className="min-w-0 flex-1">
              {item.label}
              {item.disabled && item.reason && (
                <span
                  id={`${item.key}-reason`}
                  className="mt-0.5 block text-label text-text-tertiary"
                >
                  {item.reason}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </motion.div>
  );
}
